import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api.js';
import VersionStamp from '../components/VersionStamp.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useAuth } from '../lib/auth.jsx';
import { useProfile } from '../lib/profile.jsx';
import { useTheme } from '../lib/theme.jsx';
import { vtNavigate } from '../lib/viewTransition.js';
import { useSheetDrag } from '../lib/useSheetDrag.js';
import { applyBrand, resetBrand, useBrandLogo } from '../lib/brand.js';
import { useAccess, PERMS } from '../lib/access.js';
import { useMyFlags, flagOn } from '../lib/flags.js';
import { FEATURES, owlNativeChatEnabled, fanOwlSettingsEnabled } from '../lib/features.js';
import AnalystDrawer from '../components/AnalystDrawer.jsx';
import OwlChat from '../components/OwlChat.jsx';
import StatusNoticeBanner from '../components/StatusNoticeBanner.jsx';
import DemoBanner from '../components/DemoBanner.jsx';
import ProductSurveyPrompt from '../components/ProductSurveyPrompt.jsx';
import { openReport } from '../components/ReportWidget.jsx';
import AiMark from '../components/AiMark.jsx';
import Icon from '../components/Icon.jsx';

// Persistent client shell: a left sidebar tree of Suites → Sets → Dashboards,
// with the selected dashboard rendered in the main area.
export default function ClientLayout() {
  const { suiteId, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user, isAdmin } = useAuth();
  const [suites, setSuites] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [details, setDetails] = useState({}); // suiteId -> { sets:[{id,name,dashboards}] }
  // Expanded state survives reloads — the tree reopens the way you left it.
  const [openSuites, setOpenSuites] = useState(() => readJson('howler_nav_open_suites'));
  const [openSets, setOpenSets] = useState(() => readJson('howler_nav_open_sets'));
  useEffect(() => { localStorage.setItem('howler_nav_open_suites', JSON.stringify(openSuites)); }, [openSuites]);
  useEffect(() => { localStorage.setItem('howler_nav_open_sets', JSON.stringify(openSets)); }, [openSets]);
  const [q, setQ] = useState(''); // sidebar search
  const searching = q.trim().length > 0;
  const [loading, setLoading] = useState(true);
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const [askOpen, setAskOpen] = useState(false); // the AI drawer (native Owl/Spark chat, or the Inventive analyst)
  const [askSurface, setAskSurface] = useState('analyst'); // which surface the drawer shows: 'native' | 'analyst'
  const [owlDialOpen, setOwlDialOpen] = useState(false); // the FAB's Spark / Owl fan-out (dual mode only)
  const [prewarmAsk, setPrewarmAsk] = useState(false); // load the analyst on owl hover → instant first open
  // Open the AI drawer on a surface. Default: the native chat when this person has
  // it, else the Inventive analyst (the old either/or swap). Opening the analyst
  // best-effort asks the browser to grant Inventive first-party storage access (so
  // the embed can run at first-party speed) — harmless / silent if unsupported.
  const saTriedRef = useRef(false);
  const openAsk = (surface) => {
    const s = surface || (owlNativeChatEnabled(user) ? 'native' : 'analyst');
    if (s === 'analyst') {
      if (!saTriedRef.current) {
        saTriedRef.current = true;
        try { document.requestStorageAccessFor?.('https://app.madeinventive.com').catch(() => {}); } catch { /* ignore */ }
      }
      setPrewarmAsk(true);
    }
    setAskSurface(s);
    setOwlDialOpen(false);
    setAskOpen(true);
  };
  // The top-header "Owl Data Analyst" button lives in App.jsx; it opens the drawer
  // via this event (keeps the drawer state here without lifting it up).
  useEffect(() => {
    const h = () => openAsk();
    window.addEventListener('howler:open-analyst', h);
    return () => window.removeEventListener('howler:open-analyst', h);
  }, []);
  // "Make it happen" on a home suggestion hands the campaign build to the native
  // chat (Owl or Spark — draftCampaign rides in both): open it and seed it with
  // the goal so it drafts the right audience. Only wired when the native chat is
  // on — otherwise ClientHome falls back to the campaign editor itself, so this
  // listener never fires for those users.
  const [owlSeed, setOwlSeed] = useState(null);
  useEffect(() => {
    const h = (e) => { if (!owlNativeChatEnabled(user)) return; setOwlSeed(e.detail || null); openAsk('native'); };
    window.addEventListener('howler:owl-build', h);
    return () => window.removeEventListener('howler:owl-build', h);
  }, [user]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('howler_nav_collapsed') === '1'); // desktop
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem('howler_nav_collapsed', c ? '0' : '1'); return !c; });
  const navDrag = useSheetDrag(() => setNavOpen(false)); // mobile bottom-sheet dismiss
  // The dashboard page (ViewPage) portals its actions into the menu bar so
  // Summary / Filters / ⋯ sit on the ☰ Menu line, not a separate row below.
  const [actionsSlot, setActionsSlot] = useState(null);
  const [titleSlot, setTitleSlot] = useState(null); // section pages portal their title into the menu bar

  useEffect(() => {
    let cancelled = false;
    // Event timing (Upcoming/Past) resolves from Looker in the background, so a
    // suite's first fetch can come back timing:'unknown' (cold cache) and render
    // flat. If so, refetch once shortly after to pick up the warmed dates and
    // group — no manual reload needed.
    // Timing warms on the server in the background; the first few fetches can still
    // read 'unknown'. Retry a handful of times with a growing delay so a slow Looker
    // resolve still lands the Upcoming/Past grouping without a manual reload.
    const load = (tries) => api.mySuites().then((res) => {
      if (cancelled) return;
      const list = Array.isArray(res) ? res : [];
      setSuites(list);
      if (tries > 0 && list.some((s) => !s.timing || s.timing === 'unknown')) {
        setTimeout(() => { if (!cancelled) load(tries - 1); }, 2500 + (4 - tries) * 1500);
      }
    }).catch(() => {}).finally(() => setLoading(false));
    load(4);
    return () => { cancelled = true; };
  }, []);
  useEffect(() => { api.mySettlements().then((r) => setSettlements(Array.isArray(r) ? r : [])).catch(() => {}); }, []);
  // Event Ops is a per-client pilot: which of my entities have it switched on (gates the nav).
  const [eopEntities, setEopEntities] = useState([]);
  useEffect(() => { api.eventopsEnabled().then((r) => setEopEntities(r.entities || [])).catch(() => {}); }, []);
  const onEventOps = location.pathname.startsWith('/event-ops');
  const onEventMap = location.pathname.startsWith('/event-map');
  const onEventMedia = location.pathname.startsWith('/event-media');
  const onLineup = location.pathname.startsWith('/lineup');
  const onGoals = location.pathname.startsWith('/goals');
  const onAlerts = location.pathname.startsWith('/alerts');
  const onSocial = location.pathname.startsWith('/social');
  const onAppAnalytics = location.pathname.startsWith('/app-analytics');
  const onAnalystActivity = location.pathname.startsWith('/analyst-activity');
  const onSettlements = location.pathname.startsWith('/settlements');
  const onBudget = location.pathname.startsWith('/budget');
  const onInbox = location.pathname.startsWith('/inbox');
  // On a phone the AI drawer is full-screen and this layout outlives route
  // changes, so a back gesture / nav tap used to leave it stuck open over the
  // new page. Navigating closes it (desktop keeps it docked while you browse).
  useEffect(() => { if (isMobile) { setAskOpen(false); setOwlDialOpen(false); } }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const onDigests = location.pathname.startsWith('/digests');
  const onDrive = location.pathname.startsWith('/drive');
  const onReports = location.pathname.startsWith('/reports');
  // Engage hub tabs: Campaigns (/engage/campaigns, default /engage) + Segments.
  // Engage → App (/engage/app) is its own section, not a Campaigns tab.
  const onSegments = location.pathname.startsWith('/segments') || location.pathname.startsWith('/engage/segments');
  const onEngageApp = location.pathname.startsWith('/engage/app') || location.pathname.startsWith('/engage/community');
  const onWebsite = location.pathname.startsWith('/engage/website'); // contact forms, sign-ups, pop-ups (EngagePage → WebsiteHub)
  const onConversations = location.pathname.startsWith('/conversations');
  const onActionsHub = location.pathname.startsWith('/actions-hub'); // the Actions hub (Owl-actionable suggestions)
  const onActions = (location.pathname.startsWith('/actions') || location.pathname.startsWith('/engage')) && !onSegments && !onEngageApp && !onActionsHub && !onWebsite;

  // Experience OS inbox: unread + must-acknowledge counts for the badge/banner.
  const [inbox, setInbox] = useState({ enabled: false, unread: 0, pending: [] });
  // Fans waiting on a person (Conversations). A number on the row is the point:
  // a hand-off is somebody sitting there, and nobody watches a page they have no
  // reason to open. `flash` fires once when the count RISES, so a message that
  // arrives while you are elsewhere in Pulse catches the eye.
  const [waiting, setWaiting] = useState({ unread: 0, flash: 0 });

  async function ensureDetail(sid) {
    if (details[sid]) return;
    const d = await api.mySuite(sid).catch(() => null);
    if (d) setDetails((prev) => ({ ...prev, [sid]: d }));
  }
  function toggleSuite(sid) {
    setOpenSuites((p) => ({ ...p, [sid]: !p[sid] }));
    ensureDetail(sid);
  }

  // Hydrate details for suites that were left open, and for everything once a
  // search starts (search needs every suite's tree to match against).
  useEffect(() => {
    for (const su of suites) if (openSuites[su.id] || searching) ensureDetail(su.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suites, searching]);

  // A dashboard was attached to (or detached from) a suite in Settings →
  // Dashboards / the admin suite editor — pull that suite's tree again so the
  // sidebar reflects it without a page reload.
  useEffect(() => {
    const onNavChanged = (e) => {
      const sid = e.detail?.suiteId;
      if (!sid) return;
      api.mySuite(sid).then((d) => { if (d) setDetails((prev) => ({ ...prev, [sid]: d })); }).catch(() => {});
    };
    window.addEventListener('pulse-suite-nav-changed', onNavChanged);
    return () => window.removeEventListener('pulse-suite-nav-changed', onNavChanged);
  }, []);

  // Auto-expand the suite (and its sets) for the active dashboard.
  useEffect(() => {
    if (!suiteId) return;
    setOpenSuites((p) => ({ ...p, [suiteId]: true }));
    ensureDetail(suiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiteId]);
  useEffect(() => {
    const d = details[suiteId];
    if (!d || !id) return;
    for (const set of d.sets) {
      if (set.dashboards.some((x) => x.id === id || (x.children || []).some((c) => c.id === id))) {
        setOpenSets((p) => ({ ...p, [set.id]: true }));
      }
    }
     
  }, [details, suiteId, id]);

  const go = (sid, did) => {
    if (!(sid === suiteId && did === id)) vtNavigate(navigate, `/suite/${sid}/d/${did}`);
    if (isMobile) setNavOpen(false);
  };

  // Sliding active indicator: track the active dashboard button and glide a
  // single pill to its position whenever the selection/layout changes.
  const navRef = useRef(null);
  const activeRef = useRef(null);
  const [indicator, setIndicator] = useState({ y: 0, h: 0, show: false });
  const measure = () => {
    const btn = activeRef.current; const nav = navRef.current;
    if (!btn || !nav) { setIndicator((i) => ({ ...i, show: false })); return; }
    // Hide the indicator when the active row lives inside a COLLAPSED section
    // (its suite/set is closed) — otherwise it strands itself at the collapsed
    // row's position, overlapping the headers below it.
    for (let el = btn; el && el !== nav; el = el.parentElement) {
      if (el.classList?.contains('collapsey') && !el.classList.contains('open')) {
        setIndicator((i) => ({ ...i, show: false })); return;
      }
    }
    setIndicator({ y: btn.offsetTop, h: btn.offsetHeight, show: true });
  };
  useLayoutEffect(() => {
    measure();
    // Re-measure after the expand/collapse animation settles (positions shift).
    const t = setTimeout(measure, 300);
    return () => clearTimeout(t);
     
  }, [id, suiteId, openSuites, openSets, details, collapsed, loading, isMobile, navOpen, location.pathname, settlements, q]);

  // Title of the active dashboard (for the mobile menu bar) — may be a tab.
  let activeTitle = '';
  const cur = details[suiteId];
  if (cur && id) {
    outer: for (const set of cur.sets) {
      for (const dash of set.dashboards) {
        if (dash.id === id) { activeTitle = dash.title; break outer; }
        const child = (dash.children || []).find((c) => c.id === id);
        if (child) { activeTitle = child.title; break outer; }
      }
    }
  }

  // A login can hold several client profiles. The active one (shared context,
  // switched from the top-header identity) drives the whole shell — nav, theme,
  // home, digests, actions — and persists across visits. An admin in the console
  // who opens a suite is "previewing"; an admin who switched INTO a client
  // account (mode 'client') is acting as that client (no preview banner).
  const { activeEntityId: profileEntityId, mode: profileMode, enterConsole, active } = useProfile();
  const { can } = useAccess(); // role-gated nav for the active profile (admins: all)
  const previewMode = isAdmin && profileMode === 'console';
  // When an admin previews, scope EVERYTHING to the previewed client (entity) so
  // the preview faithfully shows that one account, not every client. The active
  // suite tells us the entity; we remember it (sessionStorage) so pages without
  // a suite in the URL — settlements, documents — stay scoped too.
  const [previewEntityId, setPreviewEntityId] = useState(() => sessionStorage.getItem('howler_preview_entity') || null);
  const urlSuite = isAdmin && suiteId ? suites.find((s) => s.id === suiteId) : null;
  // …except a suite SHARED with the client currently held: it renders under that
  // client (read-only), so opening one of its dashboards must NOT flip the
  // preview to the suite's owner (Shimza → Anything Goes, 2026-09-09).
  const heldEntityId = previewMode ? previewEntityId : profileEntityId;
  const sharedToHeld = !!(urlSuite && heldEntityId && urlSuite.entityId !== heldEntityId && (urlSuite.shares || []).some((x) => x.viewerEntityId === heldEntityId));
  const suiteEntityId = urlSuite ? (sharedToHeld ? heldEntityId : urlSuite.entityId) : null;
  useEffect(() => {
    if (suiteEntityId && suiteEntityId !== previewEntityId) {
      sessionStorage.setItem('howler_preview_entity', suiteEntityId);
      setPreviewEntityId(suiteEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiteEntityId]);
  const activeEntityId = isAdmin
    ? (previewMode ? (suiteEntityId || previewEntityId) : (suiteEntityId || profileEntityId))
    : profileEntityId;
  // The share record that puts a suite in THIS client's sidebar: a client login
  // holds the tagged row (`shared`); an admin preview holds the owner's row and
  // reads the share aimed at the client being previewed. `sharedHere` is the
  // read-only test the chips and labels hang off. Both were defined here by
  // 12d88943 and deleted — call sites left standing — by 3993cfa7's rewrite of
  // the block above: "ReferenceError: shareFor is not defined" in ClientLayout,
  // a white screen on every client page in production (2026-09-09).
  const shareFor = (s) => s.shared || (s.shares || []).find((x) => x.viewerEntityId === activeEntityId) || null;
  const sharedHere = (s) => !!s.readOnly || (!!activeEntityId && s.entityId !== activeEntityId && !!shareFor(s));
  const visibleSuites = activeEntityId ? suites.filter((s) => s.entityId === activeEntityId || !!shareFor(s)) : suites;
  const activeSuiteShared = (() => { const s = suites.find((x) => x.id === suiteId); return !!s && sharedHere(s); })();
  // Drag-to-reorder the nav suites. Order is per-CLIENT (shared) — reuses the
  // existing suite_order store; the upcoming/past grouping re-partitions on top.
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [arranging, setArranging] = useState(false); // reorder mode — grips only show when on
  const reorderSuite = (from, to) => {
    if (!from || from === to || !activeEntityId) return;
    const ent = suites.filter((s) => s.entityId === activeEntityId);
    const ids = ent.map((s) => s.id);
    if (ids.indexOf(from) < 0 || ids.indexOf(to) < 0) return;
    ids.splice(ids.indexOf(from), 1);
    ids.splice(ids.indexOf(to), 0, from);
    const byId = Object.fromEntries(ent.map((s) => [s.id, s]));
    let k = 0;
    setSuites(suites.map((s) => (s.entityId === activeEntityId ? byId[ids[k++]] : s))); // optimistic
    api.saveSuiteOrder(activeEntityId, ids).catch(() => {});
  };
  // Per-client feature flags drive which workspace sections show (server routes enforce too).
  const myFlags = useMyFlags(activeEntityId);
  const fl = (k) => flagOn(myFlags, k);
  // Spark mode: this person has the native chat but the client's owl.analysis
  // flag is OFF — the chat runs as Spark (no data tools, server-enforced) and the
  // Inventive analyst stays alive alongside it, so the FAB fans out to BOTH.
  const nativeOwl = owlNativeChatEnabled(user);
  const sparkMode = nativeOwl && !!myFlags && myFlags['owl.analysis'] === false;
  const dualOwls = sparkMode && FEATURES.ask && fl('ai.analyst');
  // Client-defined categories for the nav (flag: navcategories). A category is
  // { id, name, suiteIds:[…] }; filed events show under it (takes precedence over
  // the auto Upcoming/Past), a suite lives in at most one. All edits happen in
  // "arrange" mode and save the whole array.
  const catsEnabled = fl('navcategories');
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (!activeEntityId || !catsEnabled) { setCategories([]); return; }
    api.mySuiteCategories(activeEntityId).then((r) => setCategories(r.categories || [])).catch(() => {});
  }, [activeEntityId, catsEnabled]);
  const saveCats = (next) => { setCategories(next); if (activeEntityId) api.saveMySuiteCategories(activeEntityId, next).catch(() => {}); };
  const fileSuite = (suiteId, catId, beforeId) => {
    if (!suiteId) return;
    const next = categories.map((c) => ({ ...c, suiteIds: (c.suiteIds || []).filter((id) => id !== suiteId) }));
    if (catId) { const c = next.find((x) => x.id === catId); if (c) { const i = beforeId ? c.suiteIds.indexOf(beforeId) : -1; if (i >= 0) c.suiteIds.splice(i, 0, suiteId); else c.suiteIds.push(suiteId); } }
    saveCats(next);
  };
  const addCategory = () => { const name = (prompt('New category name (e.g. Festivals):') || '').trim(); if (name) saveCats([...categories, { id: 'c' + Math.random().toString(36).slice(2, 9), name, suiteIds: [] }]); };
  const renameCategory = (id) => { const c = categories.find((x) => x.id === id); const name = prompt('Rename category:', c?.name || ''); if (name != null) saveCats(categories.map((x) => (x.id === id ? { ...x, name: name.trim() } : x))); };
  const deleteCategory = (id) => { if (confirm('Delete this category? Its events move back to Upcoming/Past.')) saveCats(categories.filter((x) => x.id !== id)); };

  // Hide a report or a whole event from the sidebar into a collapsed "Hidden"
  // section (flag: navvisibility). View-only: nothing is deleted and dependent
  // features (segments, campaigns) keep working regardless (server/navVisibility.js).
  // Per-client (shared by every login at that client), not per-person — same
  // scope as the nav categories above.
  const hideEnabled = fl('navvisibility');
  const [hiddenOpen, setHiddenOpen] = useState(false); // top-level "Hidden events" collapsed section
  const [hiddenDashOpen, setHiddenDashOpen] = useState({}); // per-suite "Hidden reports" collapsed section
  const toggleSuiteHidden = (su, hidden) => {
    if (!activeEntityId) return;
    setSuites((prev) => prev.map((s) => (s.id === su.id ? { ...s, hidden } : s))); // optimistic
    api.toggleMyNavHidden(activeEntityId, 'suite', su.id, hidden).then(() => api.mySuites().then(setSuites)).catch(() => api.mySuites().then(setSuites).catch(() => {}));
  };
  const toggleDashboardHidden = (su, d, hidden) => {
    if (!activeEntityId) return;
    setDetails((prev) => { // optimistic
      const det = prev[su.id]; if (!det) return prev;
      const patch = (list) => list.map((x) => (x.id === d.id ? { ...x, hidden } : x));
      return { ...prev, [su.id]: { ...det, sets: det.sets.map((s) => ({ ...s, dashboards: patch(s.dashboards) })) } };
    });
    const refresh = () => api.mySuite(su.id).then((det) => { if (det) setDetails((prev) => ({ ...prev, [su.id]: det })); }).catch(() => {});
    api.toggleMyNavHidden(activeEntityId, 'dashboard', d.id, hidden).then(refresh).catch(refresh);
  };
  const visibleSettlements = activeEntityId ? settlements.filter((s) => s.entityId === activeEntityId) : settlements;
  // Show the Event Ops nav only when the active client has the pilot switched on AND the
  // user may operate it (admins always pass `can`). Server enforces the real boundary.
  const eventopsOn = (activeEntityId ? eopEntities.includes(activeEntityId) : eopEntities.length > 0) && can(PERMS.EVENTOPS_MANAGE);
  // The "Event Ops" role: can operate Event Ops but has no dashboards — show ONLY Event Ops.
  const opsOnly = can(PERMS.EVENTOPS_MANAGE) && !can(PERMS.DASHBOARDS_VIEW) && !isAdmin;
  // Scope options for the Owl picker: the clients (organisers) + events this user
  // can pick. Admins get all; a client gets their own entity/events.
  const owlClients = (() => {
    const m = new Map();
    for (const s of suites) if (s.entityId) m.set(s.entityId, s.entityName || s.entityId);
    for (const e of (user?.entities || [])) m.set(e.id, e.name || e.id);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  })();
  const owlEvents = suites.map((s) => ({ id: s.id, name: s.name, entityId: s.entityId, onSale: !!s.onSale, hasGoals: !!s.hasGoals }));

  // Experience OS inbox badge/banner — scoped to the ACTIVE profile so a
  // multi-profile login only sees the current client's messages (re-polls when
  // you switch profile). Admin console (no active entity) sees all.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.osInbox(activeEntityId || undefined);
        // Campaigns awaiting this client's go-ahead also count toward the badge,
        // and ones awaiting THIS user's sign-off drive the approval banner.
        let pendingApproval = 0; let awaitingMyApproval = { count: 0, first: '' }; let myOutcomes = [];
        if (activeEntityId) { try { const sum = await api.getActionsSummary(activeEntityId); pendingApproval = sum.pendingApproval || 0; awaitingMyApproval = sum.awaitingMyApproval || awaitingMyApproval; myOutcomes = sum.myOutcomes || []; } catch { /* ignore */ } }
        if (!alive) return;
        setInbox((s) => ({ ...s, enabled: true, unread: r.unread, pendingApproval, awaitingMyApproval, myOutcomes, pending: r.threads.filter((t) => t.priority === 'must_ack' && !t.acked) }));
        // Mirror unread + pending approvals onto the installed app's icon badge.
        const total = (r.unread || 0) + pendingApproval;
        try { if (navigator.setAppBadge) { total ? navigator.setAppBadge(total) : navigator.clearAppBadge(); } } catch { /* unsupported */ }
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 20000); // keep the badge fresh
    window.addEventListener('os-refresh', poll); // instant update after ack/reply
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => { alive = false; clearInterval(t); window.removeEventListener('os-refresh', poll); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
     
  }, [location.pathname, activeEntityId]);

  // Fans waiting for a person, on either channel. Asking is also how Pulse knows
  // someone is AT the desk: the server stamps presence on this call, so "talk to
  // a person" promises live only while a team actually has Pulse open.
  const waHandoffOn = flagOn(myFlags, 'fanowl.whatsapp');
  const webHandoffOn = flagOn(myFlags, 'fanowl.webHandoff');
  useEffect(() => {
    if (!activeEntityId || (!waHandoffOn && !webHandoffOn)) { setWaiting({ unread: 0, flash: 0 }); return undefined; }
    let alive = true;
    const poll = async () => {
      const [wa, web] = await Promise.all([
        waHandoffOn ? api.waHandoffUnread('my', activeEntityId).catch(() => null) : null,
        webHandoffOn ? api.webHandoffUnread('my', activeEntityId).catch(() => null) : null,
      ]);
      if (!alive) return;
      const n = ((wa && wa.unread) || 0) + ((web && web.unread) || 0);
      setWaiting((prev) => (n === prev.unread ? prev : { unread: n, flash: n > prev.unread ? prev.flash + 1 : prev.flash }));
    };
    poll();
    const t = setInterval(poll, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [activeEntityId, waHandoffOn, webHandoffOn]);

  // White-label: apply the active client's brand pair (primary + secondary) to
  // the whole shell — UI accents AND chart palettes follow it. Clients theme to
  // their own entity; an admin preview themes to the previewed client (faithful
  // preview). Reverts to Howler on unmount / when no entity is in context.
  const themeEntityId = activeEntityId;
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (!themeEntityId) { resetBrand(); return; }
      // While viewing an event, theme to that EVENT's branding (its logo/colours,
      // inheriting the client's where unset); the home/portfolio uses the client.
      api.getEntityTheme(themeEntityId, suiteId || '')
        .then((t) => { if (alive) applyBrand(t); })
        .catch(() => { if (alive) resetBrand(); });
    };
    load();
    // Re-pull the moment branding is saved (client or this event), so the shell
    // updates live without a reload.
    const onSaved = (e) => { if (!e.detail || e.detail.entityId === themeEntityId || (e.detail.suiteId && e.detail.suiteId === suiteId)) load(); };
    window.addEventListener('pulse-branding-saved', onSaved);
    return () => { alive = false; resetBrand(); window.removeEventListener('pulse-branding-saved', onSaved); };
  }, [themeEntityId, suiteId]);

  // Mobile sheet skips the suite level for single-suite clients — make sure
  // that suite's detail is loaded the moment the sheet opens.
  useEffect(() => {
    if (isMobile && navOpen && visibleSuites.length === 1) ensureDetail(visibleSuites[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, navOpen, visibleSuites.length]);

  // When the visible suites all belong to one client, show that client's brand
  // (logo / name) at the top of the sidebar.
  const uniqueEntityIds = [...new Set(visibleSuites.map((s) => s.entityId))];
  const brand = uniqueEntityIds.length === 1 ? visibleSuites.find((s) => s.entityId === uniqueEntityIds[0]) : null;

  // Sidebar search: filters suites → sets → dashboards (including tab titles).
  // Returns the sets to render for a suite; null hides the suite, undefined
  // means "matches but still loading".
  const ql = q.trim().toLowerCase();
  const hit = (s) => String(s || '').toLowerCase().includes(ql);
  const suiteSets = (su) => {
    const det = details[su.id];
    if (!searching) return det ? det.sets : undefined;
    if (hit(su.name)) return det ? det.sets : undefined;
    if (!det) return null;
    const sets = det.sets.map((set) => {
      if (hit(set.name)) return set;
      const dashboards = set.dashboards.filter((d) => hit(d.title) || (d.children || []).some((c) => hit(c.title)));
      return dashboards.length ? { ...set, dashboards } : null;
    }).filter(Boolean);
    return sets.length ? sets : null;
  };
  // A hidden suite (server/navVisibility.js) stays out of the main groups —
  // it renders only in the "Hidden" section below — but a search still finds it,
  // same as everything else: hiding tidies the default view, it doesn't hide
  // the item from someone actively looking for it.
  const shownSuites = searching
    ? visibleSuites.filter((su) => suiteSets(su) !== null)
    : (hideEnabled ? visibleSuites.filter((su) => !su.hidden) : visibleSuites);

  // Shell = frosted column: scrollable tree on top, profile pinned at bottom.
  // ─── Workspace nav — ONE model, two renderers (desktop sidebar + phone sheet) ───
  // Grouped by job: Event (run it) → Engage (reach fans) → Insights (how it's
  // going) → Money & Files. Inbox sits on its own above them. Every item keeps
  // its own flag/permission gate; a section with nothing visible disappears.
  // Help items (Help & videos, Product, Report an issue, Your journey) live in
  // the footer's "?" menu. Section open/closed state persists per browser; a
  // closed section still opens itself when you're on one of its pages.
  const settingsSection = location.pathname.startsWith('/settings') ? new URLSearchParams(location.search).get('section') : null;
  const engageOn = !opsOnly && fl('engage') && can(PERMS.CAMPAIGNS_VIEW);
  // Fan Owl is configured in Settings — the nav deep-links there.
  const fanOwlOn = !opsOnly && !!activeEntityId && fl('fanowl') && can(PERMS.INTEGRATIONS_MANAGE) && fanOwlSettingsEnabled(user);
  const brandChip = (n) => <span style={{ ...countChip, background: 'var(--brand)', color: '#fff' }}>{n}</span>;
  const navSections = [
    { key: 'inbox', items: [
      { key: 'inbox', label: 'Inbox', icon: 'inbox', path: '/inbox', show: !opsOnly && inbox.enabled, active: onInbox, badge: inbox.unread > 0 && brandChip(inbox.unread) },
    ] },
    { key: 'event', label: 'Event', fam: 'var(--fam-mid)', items: [
      { key: 'eventops', label: 'Event Ops', icon: 'eventops', path: '/event-ops', show: eventopsOn, active: onEventOps },
      { key: 'eventmap', label: 'Event Map', icon: 'map', path: '/event-map', show: fl('mapstudio') && can(PERMS.MAP_MANAGE), active: onEventMap },
      { key: 'lineup', label: 'Lineup', icon: 'lineup', path: '/lineup', show: fl('lineup.manager') && can(PERMS.MAP_MANAGE), active: onLineup },
      { key: 'eventmedia', label: 'Event Media', icon: 'media', path: '/event-media', show: fl('eventassets') && can(PERMS.MAP_MANAGE), active: onEventMedia },
    ] },
    { key: 'engage', label: 'Engage', fam: 'var(--brand-2)', items: [
      { key: 'app', label: 'App', icon: 'app', path: '/engage/app', show: engageOn && fl('community'), active: onEngageApp },
      { key: 'actionshub', label: 'Actions', icon: 'spark', path: '/actions-hub', show: engageOn && fl('actionshub'), active: onActionsHub },
      { key: 'campaigns', label: 'Campaigns', icon: 'campaigns', path: '/engage/campaigns', show: engageOn && fl('engage.campaigns'), active: onActions },
      { key: 'segments', label: 'Segments', icon: 'segments', path: '/engage/segments', show: engageOn && fl('engage.segments'), active: onSegments },
      { key: 'fanowl', label: 'Fan Owl', icon: 'owl', path: '/settings?section=fanowl', show: fanOwlOn, active: settingsSection === 'fanowl' },
      // What fans say back. Two independent sources, either one earns the row.
      { key: 'conversations', label: 'Conversations', icon: 'chat', path: '/conversations', show: engageOn && (fl('community.inbox') || fl('fanowl.whatsapp') || fl('fanowl.webHandoff') || (fl('fanowl.inbox') && fl('fanowl.transcripts'))), active: onConversations,
        badge: waiting.unread > 0 && <span key={waiting.flash} style={{ ...countChip, background: 'var(--brand)', color: '#fff', '--live-ring': 'color-mix(in srgb, var(--brand) 55%, transparent)', animation: 'howlerPulse 1.5s ease-out 3' }}>{waiting.unread}</span> },
      // Website: contact forms, sign-ups and pop-ups (EngagePage → WebsiteHub); shown while any part is on.
      { key: 'website', label: 'Website', icon: 'web', path: '/engage/website', show: engageOn && (fl('engage.contact_forms') || fl('engage.newsletter_forms') || fl('engage.site_promos')), active: onWebsite },
    ] },
    { key: 'insights', label: 'Insights', fam: 'var(--brand)', items: [
      { key: 'goals', label: 'Goals', icon: 'goals', path: '/goals', show: !opsOnly && fl('goals') && (visibleSuites.length > 0 || isAdmin), active: onGoals },
      // App analytics: the client's events inside the Howler consumer app (PostHog) —
      // flag-gated only, never on engage flags or permissions.
      { key: 'appanalytics', label: 'App Analytics', icon: 'app', path: '/app-analytics', show: !opsOnly && fl('appanalytics'), active: onAppAnalytics },
      // Social: reporting on the client's own accounts — insight, but still behind the engage gate it always had.
      { key: 'social', label: 'Social Analytics', icon: 'social', path: '/social', show: engageOn && fl('social'), active: onSocial },
      // How the team is using the AI analyst — a report, flag-gated only.
      { key: 'owlactivity', label: 'Owl Activity', icon: 'owl', path: '/analyst-activity', show: !opsOnly && fl('ai.analyst_usage'), active: onAnalystActivity },
      { key: 'reports', label: 'Reports', icon: 'reports', path: '/reports', show: !opsOnly && fl('reports') && can(PERMS.REPORTS_MANAGE), active: onReports },
      { key: 'alerts', label: 'Alerts', icon: 'alerts', path: '/alerts', show: !opsOnly && fl('alerts') && (visibleSuites.length > 0 || isAdmin), active: onAlerts },
      { key: 'digests', label: 'Digests', icon: 'digests', path: '/digests', show: !opsOnly && fl('digests') && can(PERMS.DIGESTS_MANAGE), active: onDigests },
    ] },
    { key: 'money', label: 'Money & Files', fam: 'var(--muted-2)', items: [
      // Settlements: hidden for clients with no reports; admins always see it (to preview the feature).
      { key: 'settlements', label: 'Settlements', icon: 'settlements', path: '/settlements', show: !opsOnly && fl('settlements') && can(PERMS.SETTLEMENTS_VIEW) && (visibleSettlements.length > 0 || isAdmin), active: onSettlements,
        badge: visibleSettlements.length > 0 && <span style={countChip}>{visibleSettlements.length}</span> },
      { key: 'budget', label: 'Budget', icon: 'budget', path: '/budget', show: !opsOnly && fl('budget') && can(PERMS.BUDGETS_VIEW), active: onBudget },
      { key: 'drive', label: 'Drive', icon: 'folder', path: '/drive', show: !opsOnly && fl('integrations.drive'), active: onDrive },
    ] },
  ];
  const [closedNav, setClosedNav] = useState(() => readJson('howler_nav_closed_sections'));
  useEffect(() => { localStorage.setItem('howler_nav_closed_sections', JSON.stringify(closedNav)); }, [closedNav]);
  const renderNav = (mobile) => {
    let firstHead = true;
    return navSections.map((sec) => {
      const items = sec.items.filter((it) => it.show);
      if (!items.length) return null;
      const open = !sec.label || !closedNav[sec.key] || items.some((it) => it.active);
      // Ops-only role has no "Suites" header (where everyone else's collapse
      // arrow lives), so the first section header carries it for them.
      const collapseHere = sec.label && firstHead && opsOnly && !mobile;
      if (sec.label) firstHead = false;
      return (
        <div key={sec.key}>
          <div style={{ borderTop: '1px solid var(--hairline)', margin: mobile ? '10px 4px' : '12px 6px 10px' }} />
          {sec.label && (
            <div className={`sb-head${mobile ? '' : ' sticky'}`} style={{ padding: mobile ? '0 8px 0 14px' : '0 8px 4px 14px' }}>
              <button onClick={() => setClosedNav((p) => ({ ...p, [sec.key]: !p[sec.key] }))} aria-expanded={open} style={{ ...secHeadBtn, minHeight: mobile ? 40 : 26 }}>
                <span className="sb-dot" style={{ '--fam': sec.fam }} />
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>{sec.label}</span>
                <Caret open={open} small />
              </button>
              {collapseHere && <button onClick={toggleCollapsed} title="Collapse sidebar" style={iconBtn}>⟨</button>}
            </div>
          )}
          {open && items.map((it) => (
            <button
              key={it.key}
              ref={it.active && !mobile ? activeRef : null}
              className={`nav-row${it.active ? ' active' : ''}`}
              style={{ ...(mobile ? mRowSuite : rowBtn), fontWeight: it.active ? (mobile ? 700 : 600) : 500 }}
              onClick={() => { if (!it.active) vtNavigate(navigate, it.path); if (isMobile) setNavOpen(false); }}
            >
              <Icon name={it.icon} size={mobile ? 18 : 17} />
              <span style={ellip}>{it.label}</span>
              {it.badge}
            </button>
          ))}
        </div>
      );
    });
  };

  const sidebar = (
    <div className="howler-sidebar" style={{ ...sidebarShell, ...(isMobile ? mobileSidebar : null) }}>
    <nav ref={navRef} className="sidebar-scroll" style={sidebarStyle}>
      <div className="nav-indicator" style={{ transform: `translateY(${indicator.y}px)`, height: indicator.h, opacity: indicator.show ? 1 : 0 }} />
      {!opsOnly && (
      <div style={searchWrap}>
        <span style={{ color: 'var(--muted)', fontSize: 13, flexShrink: 0 }}>⌕</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dashboards…" style={searchInput} />
        {searching && <button onClick={() => setQ('')} style={searchClear} aria-label="Clear search">✕</button>}
      </div>
      )}
      {!opsOnly && (
      <div className="sb-head" style={{ gap: 7, padding: '2px 8px 10px 14px' }}>
        <span className="sb-dot" style={{ '--fam': 'var(--brand)' }} />
        <span
          onDoubleClick={() => { if (!searching && activeEntityId && (visibleSuites.length > 1 || catsEnabled)) setArranging((a) => !a); }}
          title={catsEnabled ? 'Double-click to arrange events & categories' : 'Double-click to reorder events'}
          style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: arranging ? 'var(--brand)' : 'var(--muted)', cursor: 'default', userSelect: 'none' }}>
          {arranging ? 'Suites · reordering' : 'Suites'}
        </span>
        {arranging && <button onClick={() => setArranging(false)} title="Done reordering" style={{ ...iconBtn, color: 'var(--brand)', fontWeight: 700 }}>✓</button>}
        {!isMobile && <button onClick={toggleCollapsed} title="Collapse sidebar" style={iconBtn}>⟨</button>}
      </div>
      )}
      {loading ? (
        <div style={{ padding: 14, color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
      ) : opsOnly ? null : visibleSuites.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--muted)', fontSize: 13 }}>No suites assigned.</div>
      ) : searching && shownSuites.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--muted)', fontSize: 13 }}>No matches for “{q.trim()}”.</div>
      ) : (
        (() => {
        // Grouping: client CATEGORIES take precedence (filed events under their
        // category), then the automatic Upcoming/Past/Other for anything unfiled.
        // Timing resolves from Looker in the background — a suite's first load is
        // 'unknown', so with no categories we render a flat list that round.
        const catsOn = catsEnabled && !searching && !!activeEntityId;
        const byId = Object.fromEntries(shownSuites.map((s) => [s.id, s]));
        const filed = new Set();
        if (catsOn) for (const c of categories) for (const id of c.suiteIds || []) if (byId[id]) filed.add(id);
        const unfiled = shownSuites.filter((s) => !filed.has(s.id));
        const TIMING = [['upcoming', 'Upcoming'], ['past', 'Past'], ['undated', 'Other']];
        const timingGroups = TIMING.map(([key, label]) => ({ label, items: unfiled.filter((s) => s.timing === key) })).filter((g) => g.items.length);
        const anyUnknown = shownSuites.some((s) => !s.timing || s.timing === 'unknown');
        // Show the Upcoming/Past headers as soon as dates have resolved and at least one
        // REAL timing bucket exists — even if it's the only one (e.g. every event is in
        // the past). We only stay flat while searching, while dates are still resolving
        // (cold), or when everything is undated (a lone "Other" header adds nothing).
        const hasRealTiming = timingGroups.some((g) => g.label !== 'Other');
        const showTiming = !searching && !anyUnknown && hasRealTiming;
        const canReorder = arranging && !searching && !!activeEntityId; // grips only in reorder mode
        const renderSuite = (su, catId = null) => {
          const rawSets = suiteSets(su);
          // Reports a client/admin hid within this suite (server/navVisibility.js):
          // pulled out of their set's normal listing into this suite's own
          // collapsed "Hidden" subsection below, same idea as the top-level one.
          const sets = hideEnabled && rawSets ? rawSets.map((set) => ({ ...set, dashboards: set.dashboards.filter((d) => !d.hidden) })) : rawSets;
          const hiddenDash = hideEnabled && rawSets ? rawSets.flatMap((set) => set.dashboards.filter((d) => d.hidden)) : [];
          const suiteOpen = searching || !!openSuites[su.id];
          const canDragThis = canReorder && !su.hidden; // never reorder from inside the Hidden section
          return (
            <div key={su.id} style={{ marginBottom: 2, borderRadius: 8, opacity: dragId === su.id ? 0.4 : 1, boxShadow: (canDragThis && dragOverId === su.id && dragId && dragId !== su.id) ? 'inset 0 2px 0 0 var(--brand)' : 'none' }}
              onDragOver={canDragThis ? (e) => { e.preventDefault(); if (dragOverId !== su.id) setDragOverId(su.id); } : undefined}
              onDrop={canDragThis ? (e) => { e.preventDefault(); if (catId) { fileSuite(dragId, catId, su.id); } else { if (categories.some((c) => (c.suiteIds || []).includes(dragId))) fileSuite(dragId, null); reorderSuite(dragId, su.id); } setDragId(null); setDragOverId(null); } : undefined}
              onDragEnd={canDragThis ? () => { setDragId(null); setDragOverId(null); } : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {canDragThis && <span draggable onDragStart={(e) => { setDragId(su.id); e.dataTransfer.effectAllowed = 'move'; }} title="Drag to reorder / into a category" style={{ cursor: 'grab', color: 'var(--muted)', fontSize: 11, lineHeight: 1, padding: '0 1px', flexShrink: 0, userSelect: 'none' }}>⠿</span>}
                <button className="nav-row" style={{ ...rowBtn, fontWeight: 600, flex: 1, minWidth: 0 }} onClick={() => toggleSuite(su.id)}>
                  <Caret open={suiteOpen} />
                  <Ico v={su.icon} size={22} />
                  <span style={ellip}>{(sharedHere(su) && shareFor(su)?.label) || su.name}</span>
                  {sharedHere(su) && <span style={sharedChip} title={`Shared by ${shareFor(su)?.ownerName || su.entityName || 'another organiser'} — read-only`}>shared</span>}
                </button>
                {arranging && catId && <button onClick={(e) => { e.stopPropagation(); fileSuite(su.id, null); }} title="Remove from category" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13, padding: '0 4px', flexShrink: 0 }}>✕</button>}
                {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleSuiteHidden(su, !su.hidden); }} title={su.hidden ? 'Unhide this event' : 'Hide from sidebar'} style={miniIcon}><Icon name={su.hidden ? 'eye' : 'eyeoff'} size={13} /></button>}
                {su.liveDashboardId && <LiveBtn onClick={() => go(su.id, su.liveDashboardId)} title={`Live ticket sales — ${su.name}`} />}
              </div>
              <div className={`collapsey${suiteOpen ? ' open' : ''}`}>
                <div className="collapsey-inner" style={{ marginTop: 1 }}>
                  {sets === undefined ? (
                    suiteOpen && <div style={{ ...subRow, color: 'var(--muted)' }}>Loading…</div>
                  ) : (sets || []).length === 0 ? (
                    <div style={{ ...subRow, color: 'var(--muted)' }}>No sets</div>
                  ) : (
                    <>
                    {/* A shared event: the organiser's campaigns + segments for it, read-only. */}
                    {sharedHere(su) && (shareFor(su)?.includes || []).length > 0 && (
                      <button className={`nav-row${location.pathname === `/suite/${su.id}/shared` ? ' active' : ''}`} style={{ ...rowBtn, padding: '6px 12px 6px 34px', fontSize: 13, fontWeight: location.pathname === `/suite/${su.id}/shared` ? 600 : 450 }} onClick={() => { vtNavigate(navigate, `/suite/${su.id}/shared`); if (isMobile) setNavOpen(false); }}>
                        <span style={{ ...dot, background: 'rgba(0,0,0,0.18)' }} />
                        <span style={ellip}>Campaigns & segments</span>
                      </button>
                    )}
                    {/* Sales Command Centre — the event's sales, comparisons and buyers (flag salescc). */}
                    {!sharedHere(su) && fl('salescc') && (
                      <button className={`nav-row${location.pathname === `/suite/${su.id}/sales` ? ' active' : ''}`} style={{ ...rowBtn, padding: '6px 12px 6px 34px', fontSize: 13, fontWeight: location.pathname === `/suite/${su.id}/sales` ? 600 : 450 }} onClick={() => { vtNavigate(navigate, `/suite/${su.id}/sales`); if (isMobile) setNavOpen(false); }}>
                        <span style={{ ...dot, background: location.pathname === `/suite/${su.id}/sales` ? 'var(--brand)' : 'rgba(0,0,0,0.18)' }} />
                        <span style={ellip}>Sales Command Centre</span>
                      </button>
                    )}
                    {sets.map((set) => {
                      const setOpen = searching || !!openSets[set.id];
                      // Suite-level (loose) dashboards render with no set header,
                      // one indent shallower than set dashboards.
                      const dashBtn = (d, loose) => {
                        const tabs = d.children?.length || 0;
                        const active = (d.id === id || (d.children || []).some((c) => c.id === id)) && su.id === suiteId;
                        return (
                          <div key={d.id} style={{ display: 'flex', alignItems: 'center' }}>
                            <button ref={active ? activeRef : null} onClick={() => go(su.id, d.id)} className={`nav-row${active ? ' active' : ''}`} style={{ ...rowBtn, padding: loose ? '6px 12px 6px 34px' : '6px 12px 6px 50px', fontSize: 13, fontWeight: active ? 600 : 450, flex: 1, minWidth: 0 }}>
                              <span style={{ ...dot, background: active ? 'var(--brand)' : 'rgba(0,0,0,0.18)' }} />
                              <span style={ellip}>{d.title}</span>
                              {tabs > 0 && <span style={tabChip} title={`${tabs + 1} tabs inside`}>{tabs + 1}</span>}
                            </button>
                            {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleDashboardHidden(su, d, true); }} title="Hide from sidebar" style={{ ...miniIcon, marginRight: 6 }}><Icon name="eyeoff" size={13} /></button>}
                          </div>
                        );
                      };
                      if (set.direct) return <div key={set.id}>{set.dashboards.map((d) => dashBtn(d, true))}</div>;
                      return (
                        <div key={set.id}>
                          <button className="nav-row" style={{ ...rowBtn, padding: '7px 12px 7px 28px', fontWeight: 500, fontSize: 13, color: 'var(--muted-2)' }} onClick={() => setOpenSets((p) => ({ ...p, [set.id]: !p[set.id] }))}>
                            <Caret open={setOpen} small />
                            <Ico v={set.icon} size={15} />
                            <span style={ellip}>{set.name}</span>
                          </button>
                          <div className={`collapsey${setOpen ? ' open' : ''}`}>
                            <div className="collapsey-inner">
                              {set.dashboards.map((d) => dashBtn(d, false))}
                              {set.dashboards.length === 0 && <div style={{ ...subRow, paddingLeft: 50, color: 'var(--muted)' }}>No dashboards</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {hideEnabled && hiddenDash.length > 0 && (
                      <div style={{ marginTop: 2 }}>
                        <button className="nav-row" style={{ ...rowBtn, padding: '6px 12px 6px 28px', fontWeight: 500, fontSize: 12, color: 'var(--muted)' }} onClick={() => setHiddenDashOpen((p) => ({ ...p, [su.id]: !p[su.id] }))}>
                          <Caret open={!!hiddenDashOpen[su.id]} small />
                          <span style={ellip}>Hidden ({hiddenDash.length})</span>
                        </button>
                        <div className={`collapsey${hiddenDashOpen[su.id] ? ' open' : ''}`}>
                          <div className="collapsey-inner">
                            {hiddenDash.map((d) => (
                              <div key={d.id} style={{ display: 'flex', alignItems: 'center' }}>
                                <button onClick={() => go(su.id, d.id)} className="nav-row" style={{ ...rowBtn, padding: '6px 6px 6px 50px', fontSize: 13, color: 'var(--muted)', flex: 1, minWidth: 0 }}>
                                  <span style={{ ...dot, background: 'rgba(0,0,0,0.14)' }} />
                                  <span style={ellip}>{d.title}</span>
                                </button>
                                {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleDashboardHidden(su, d, false); }} title="Unhide this report" style={{ ...miniIcon, marginRight: 6 }}><Icon name="eye" size={13} /></button>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        };
        const headStyle = { padding: '7px 8px 3px 14px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted-2)' };
        const miniIcon = { border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11, padding: '0 3px', flexShrink: 0 };
        // Whole hidden events (server/navVisibility.js), collapsed at the bottom of
        // the tree — reuses renderSuite so an expanded hidden event still shows its
        // full, navigable set/dashboard tree (unhiding is optional, not required to
        // look at it). Left out of the normal groups above via `shownSuites`.
        // Suppressed while searching: search already surfaces a hidden match inline
        // (with its own unhide icon right there), a second copy here would be noise.
        const hiddenSuites = hideEnabled && !searching ? visibleSuites.filter((s) => s.hidden) : [];
        const hiddenSection = hiddenSuites.length > 0 && (
          <div key="__hidden__" style={{ marginTop: 4, marginBottom: 2 }}>
            <button className="nav-row" style={{ ...rowBtn, padding: '7px 8px 7px 14px' }} onClick={() => setHiddenOpen((o) => !o)}>
              <Caret open={hiddenOpen} small />
              <span style={{ flex: 1, ...ellip, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted-2)' }}>Hidden ({hiddenSuites.length})</span>
            </button>
            <div className={`collapsey${hiddenOpen ? ' open' : ''}`}>
              <div className="collapsey-inner">{hiddenSuites.map((s) => renderSuite(s))}</div>
            </div>
          </div>
        );
        // Flag OFF → legacy: flat while searching / dates resolving / a single group, else timing groups.
        if (!catsOn) {
          const main = !showTiming ? shownSuites.map((s) => renderSuite(s)) : timingGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}><div className="sb-head sticky" style={headStyle}><span className="sb-dot" />{g.label}</div>{g.items.map((s) => renderSuite(s))}</div>
          ));
          return (<>{main}{hiddenSection}</>);
        }
        // Flag ON: category groups (a drop target while arranging), then the unfiled
        // events. "+ New category" always shows in arrange mode so the FIRST category
        // is reachable (with zero categories it's still the way in). Unfiled render
        // flat when there are no categories and the timing groups aren't meaningful
        // yet (dates resolving / a single group).
        const flatUnfiled = categories.length === 0 && !showTiming;
        return (
          <>
            {categories.map((c) => {
              const items = (c.suiteIds || []).map((id) => byId[id]).filter(Boolean);
              if (!items.length && !arranging) return null; // hide empties unless arranging
              return (
                <div key={c.id} style={{ marginBottom: 4, borderRadius: 8, boxShadow: (canReorder && dragOverId === `cat:${c.id}` && dragId) ? 'inset 0 0 0 1.5px var(--brand)' : 'none' }}
                  onDragOver={canReorder ? (e) => { e.preventDefault(); if (dragOverId !== `cat:${c.id}`) setDragOverId(`cat:${c.id}`); } : undefined}
                  onDrop={canReorder ? (e) => { e.preventDefault(); fileSuite(dragId, c.id); setDragId(null); setDragOverId(null); } : undefined}>
                  <div style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="folder" size={15} />
                    <span style={{ flex: 1, ...ellip }}>{c.name}</span>
                    {arranging && <button onClick={() => renameCategory(c.id)} title="Rename category" style={miniIcon}>✎</button>}
                    {arranging && <button onClick={() => deleteCategory(c.id)} title="Delete category" style={miniIcon}>✕</button>}
                  </div>
                  {items.length ? items.map((s) => renderSuite(s, c.id)) : <div style={{ ...subRow, color: 'var(--muted)', fontStyle: 'italic' }}>Drop events here</div>}
                </div>
              );
            })}
            {flatUnfiled
              ? unfiled.map((s) => renderSuite(s))
              : timingGroups.map((g) => (
                <div key={g.label} style={{ marginBottom: 4 }}><div className="sb-head sticky" style={headStyle}><span className="sb-dot" />{g.label}</div>{g.items.map((s) => renderSuite(s))}</div>
              ))}
            {arranging && <button onClick={addCategory} style={{ ...subRow, color: 'var(--brand)', fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '6px 14px' }}>+ New category</button>}
            {hiddenSection}
          </>
        );
        })()
      )}
      {renderNav(false)}
    </nav>
    <ProfileFooter
      user={user}
      isAdmin={isAdmin}
      activeEntityId={activeEntityId}
      brand={brand}
      helpOn={fl('help')}
      productOn={fl('report')}
      journeyOn={!opsOnly && fl('yourjourney')}
      onNavigate={(path) => { navigate(path); if (isMobile) setNavOpen(false); }}
    />
    </div>
  );

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
      {/* Desktop: sidebar always mounted, width-animates to 0 when collapsed.
          Mobile: a drawer. */}
      {!isMobile && <div className={`sidebar-wrap${collapsed ? ' collapsed' : ''}`}>{sidebar}</div>}
      {/* Mobile: navigation is a bottom sheet (same language as the filters) —
          thumb-reachable, drag-to-dismiss, bigger touch targets. With a single
          suite the suite level is skipped entirely. */}
      {isMobile && navOpen && (
        <div className="ai-overlay" style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end' }} onClick={() => setNavOpen(false)}>
          <div className="ai-sheet" style={{ ...navSheet, ...navDrag.style }} onClick={(e) => e.stopPropagation()}>
            <div className="sheet-grip" {...navDrag.handlers} style={{ marginTop: 10 }} />
            {!opsOnly && (
            <div style={{ padding: '2px 14px 8px' }}>
              <div style={searchWrap}>
                <span style={{ color: 'var(--muted)', fontSize: 13, flexShrink: 0 }}>⌕</span>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dashboards…" style={searchInput} />
                {searching && <button onClick={() => setQ('')} style={searchClear} aria-label="Clear search">✕</button>}
              </div>
            </div>
            )}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 10px 8px' }}>
              {searching && shownSuites.length === 0 && (
                <div style={{ padding: 14, color: 'var(--muted)', fontSize: 13 }}>No matches for “{q.trim()}”.</div>
              )}
              {!opsOnly && shownSuites.map((su) => {
                const rawSets = suiteSets(su);
                // Mobile parity with the desktop tree: a hidden report is pulled
                // out of its set's normal listing into this suite's own collapsed
                // "Hidden" row below, never shown in full (server/navVisibility.js).
                const sets = hideEnabled && rawSets ? rawSets.map((set) => ({ ...set, dashboards: set.dashboards.filter((d) => !d.hidden) })) : rawSets;
                const hiddenDash = hideEnabled && rawSets ? rawSets.flatMap((set) => set.dashboards.filter((d) => d.hidden)) : [];
                const single = shownSuites.length === 1;
                const suiteOpen = single || searching || !!openSuites[su.id];
                return (
                  <div key={su.id}>
                    {!single && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button className="nav-row" style={{ ...mRowSuite, flex: 1, minWidth: 0 }} onClick={() => toggleSuite(su.id)}>
                          <Caret open={suiteOpen} />
                          <Ico v={su.icon} size={22} />
                          <span style={ellip}>{su.name}</span>
                        </button>
                        {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleSuiteHidden(su, !su.hidden); }} title={su.hidden ? 'Unhide this event' : 'Hide from sidebar'} style={mHideBtn}><Icon name={su.hidden ? 'eye' : 'eyeoff'} size={16} /></button>}
                        {su.liveDashboardId && <LiveBtn onClick={() => go(su.id, su.liveDashboardId)} title={`Live ticket sales — ${su.name}`} />}
                      </div>
                    )}
                    {single && (su.liveDashboardId || hideEnabled) && (
                      <div style={{ padding: '0 12px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {su.liveDashboardId && <LiveBtn wide onClick={() => go(su.id, su.liveDashboardId)} title={`Live ticket sales — ${su.name}`} />}
                        {hideEnabled && <button onClick={() => toggleSuiteHidden(su, !su.hidden)} title={su.hidden ? 'Unhide this event' : 'Hide from sidebar'} style={mHideBtn}><Icon name={su.hidden ? 'eye' : 'eyeoff'} size={16} /></button>}
                      </div>
                    )}
                    {suiteOpen && (
                      sets === undefined ? (
                        <div style={{ padding: '8px 14px', color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
                      ) : (
                        <>
                        {!sharedHere(su) && fl('salescc') && (() => {
                          const active = location.pathname === `/suite/${su.id}/sales`;
                          return (
                            <button onClick={() => { vtNavigate(navigate, `/suite/${su.id}/sales`); setNavOpen(false); }} className={`nav-row${active ? ' active' : ''}`} style={{ ...mRowDash, paddingLeft: single ? 20 : 34, fontWeight: active ? 700 : 450, width: '100%' }}>
                              <span style={{ ...dot, background: active ? 'var(--brand)' : 'rgba(128,128,128,0.35)' }} />
                              <span style={ellip}>Sales Command Centre</span>
                            </button>
                          );
                        })()}
                        {(sets || []).map((set) => {
                          const setOpen = searching || !!openSets[set.id];
                          const dashBtn = (d, loose) => {
                            const tabs = d.children?.length || 0;
                            const active = (d.id === id || (d.children || []).some((c) => c.id === id)) && su.id === suiteId;
                            return (
                              <div key={d.id} style={{ display: 'flex', alignItems: 'center' }}>
                                <button onClick={() => go(su.id, d.id)} className={`nav-row${active ? ' active' : ''}`} style={{ ...mRowDash, paddingLeft: loose ? (single ? 20 : 34) : (single ? 34 : 48), fontWeight: active ? 700 : 450, flex: 1, minWidth: 0 }}>
                                  <span style={{ ...dot, background: active ? 'var(--brand)' : 'rgba(128,128,128,0.35)' }} />
                                  <span style={ellip}>{d.title}</span>
                                  {tabs > 0 && <span style={tabChip}>{tabs + 1}</span>}
                                </button>
                                {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleDashboardHidden(su, d, true); }} title="Hide from sidebar" style={mHideBtn}><Icon name="eyeoff" size={16} /></button>}
                              </div>
                            );
                          };
                          if (set.direct) return <div key={set.id}>{set.dashboards.map((d) => dashBtn(d, true))}</div>;
                          return (
                            <div key={set.id}>
                              <button className="nav-row" style={{ ...mRowSet, paddingLeft: single ? 12 : 26 }} onClick={() => setOpenSets((p) => ({ ...p, [set.id]: !p[set.id] }))}>
                                <Caret open={setOpen} small />
                                <Ico v={set.icon} size={17} />
                                <span style={ellip}>{set.name}</span>
                              </button>
                              {setOpen && set.dashboards.map((d) => dashBtn(d, false))}
                            </div>
                          );
                        })}
                        {hideEnabled && hiddenDash.length > 0 && (
                          <div>
                            <button className="nav-row" style={{ ...mRowSet, paddingLeft: single ? 12 : 26, fontWeight: 500 }} onClick={() => setHiddenDashOpen((p) => ({ ...p, [su.id]: !p[su.id] }))}>
                              <Caret open={!!hiddenDashOpen[su.id]} small />
                              <span style={ellip}>Hidden ({hiddenDash.length})</span>
                            </button>
                            {hiddenDashOpen[su.id] && hiddenDash.map((d) => (
                              <div key={d.id} style={{ display: 'flex', alignItems: 'center' }}>
                                <button onClick={() => go(su.id, d.id)} className="nav-row" style={{ ...mRowDash, paddingLeft: single ? 34 : 48, color: 'var(--muted)', flex: 1, minWidth: 0 }}>
                                  <span style={{ ...dot, background: 'rgba(128,128,128,0.2)' }} />
                                  <span style={ellip}>{d.title}</span>
                                </button>
                                {hideEnabled && <button onClick={(e) => { e.stopPropagation(); toggleDashboardHidden(su, d, false); }} title="Unhide this report" style={mHideBtn}><Icon name="eye" size={16} /></button>}
                              </div>
                            ))}
                          </div>
                        )}
                        </>
                      )
                    )}
                  </div>
                );
              })}
              {hideEnabled && !searching && !opsOnly && (() => {
                const hiddenSuitesM = visibleSuites.filter((s) => s.hidden);
                if (!hiddenSuitesM.length) return null;
                return (
                  <div>
                    <button className="nav-row" style={{ ...mRowSuite, fontSize: 13, fontWeight: 600, color: 'var(--muted-2)' }} onClick={() => setHiddenOpen((o) => !o)}>
                      <Caret open={hiddenOpen} small />
                      <span style={ellip}>Hidden ({hiddenSuitesM.length})</span>
                    </button>
                    {hiddenOpen && hiddenSuitesM.map((su) => (
                      <div key={su.id} style={{ ...mRowDash, paddingLeft: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', cursor: 'default' }}>
                        <Ico v={su.icon} size={18} />
                        <span style={{ ...ellip, flex: 1, minWidth: 0 }}>{su.name}</span>
                        {hideEnabled && <button onClick={() => toggleSuiteHidden(su, false)} title="Unhide this event" style={mHideBtn}><Icon name="eye" size={16} /></button>}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {renderNav(true)}
            </div>
            <ProfileFooter
              user={user}
              isAdmin={isAdmin}
              activeEntityId={activeEntityId}
              brand={brand}
              helpOn={fl('help')}
      productOn={fl('report')}
      journeyOn={!opsOnly && fl('yourjourney')}
              onNavigate={(path) => { navigate(path); setNavOpen(false); }}
            />
          </div>
        </div>
      )}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Platform status notices — active incidents (and recently-resolved ones)
            affecting this client. Self-contained: polls /api/my/notices. */}
        <StatusNoticeBanner />
        {/* Demo watermark — permanent (never dismissible) on the fictional demo
            client, so a screenshot can't pass as a real client's numbers. Renders
            nothing for everyone else. */}
        <DemoBanner entityId={activeEntityId} />
        {/* "Quick question from Howler" — the one product survey card this
            person should see in this client, if any. Flag-gated per client
            (pulsesurveys; the server enforces it too). Self-contained. */}
        <ProductSurveyPrompt entityId={activeEntityId} enabled={fl('pulsesurveys')} preview={isAdmin} />
        {/* Must-acknowledge banner — Howler messages that need a response,
            persistent until acknowledged. Tapping opens the inbox. */}
        {!onInbox && inbox.pending.length > 0 && (
          <button
            onClick={() => vtNavigate(navigate, '/inbox')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '10px 16px', background: 'linear-gradient(90deg, var(--brand), var(--brand-2))', color: '#fff', fontSize: 13 }}
          >
            <Icon name="campaigns" size={16} />
            <span style={{ fontWeight: 700 }}>{inbox.pending.length === 1 ? 'A message from Howler needs your acknowledgement' : `${inbox.pending.length} messages from Howler need your acknowledgement`}</span>
            {inbox.pending[0]?.title && inbox.pending.length === 1 && <span style={{ opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {inbox.pending[0].title}</span>}
            <span style={{ flex: 1 }} />
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 980, padding: '5px 14px', fontWeight: 700, flexShrink: 0 }}>Open →</span>
          </button>
        )}
        {/* Approval banner — campaigns awaiting THIS user's sign-off. Persistent
            until cleared; taps through to the campaign. */}
        {!location.pathname.startsWith('/actions') && (inbox.awaitingMyApproval?.count > 0) && (
          <button
            onClick={() => vtNavigate(navigate, inbox.awaitingMyApproval.first ? `/actions?action=${inbox.awaitingMyApproval.first}` : '/actions')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '10px 16px', background: 'linear-gradient(90deg, #f59e0b, #ea580c)', color: '#fff', fontSize: 13 }}
          >
            <Icon name="pending" size={16} />
            <span style={{ fontWeight: 700 }}>{inbox.awaitingMyApproval.count === 1 ? 'A campaign is waiting for your approval' : `${inbox.awaitingMyApproval.count} campaigns are waiting for your approval`}</span>
            <span style={{ flex: 1 }} />
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 980, padding: '5px 14px', fontWeight: 700, flexShrink: 0 }}>Review →</span>
          </button>
        )}
        {/* Approval-outcome banner — ALWAYS shown to the campaign's creator when a
            request is approved or sent back, until they acknowledge it. */}
        {(inbox.myOutcomes || []).map((o) => {
          const approved = o.outcome === 'approved';
          const ack = () => { api.ackCampaignOutcome(activeEntityId, o.id).catch(() => {}); setInbox((s) => ({ ...s, myOutcomes: (s.myOutcomes || []).filter((x) => x.id !== o.id) })); };
          return (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', color: '#fff', fontSize: 13, background: approved ? 'linear-gradient(90deg, #16a34a, #15803d)' : 'linear-gradient(90deg, #f59e0b, #dc2626)' }}>
              <Icon name={approved ? 'check' : 'undo'} size={16} />
              <button onClick={() => { ack(); vtNavigate(navigate, `/actions?action=${o.id}`); }} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>“{o.title}” was {approved ? 'approved' : 'sent back to draft'}{o.by ? ` by ${o.by}` : ''}{approved ? (o.readyToSend ? ' — ready for you to send' : ' — now sending') : ''}.</span>
                {!approved && o.note ? <span style={{ opacity: 0.9 }}> {o.note}</span> : null}
              </button>
              <span onClick={() => { ack(); vtNavigate(navigate, `/actions?action=${o.id}`); }} style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 980, padding: '5px 14px', fontWeight: 700, flexShrink: 0, cursor: 'pointer' }}>Open →</span>
              <button onClick={ack} title="Dismiss" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 17, lineHeight: 1, flexShrink: 0, opacity: 0.85 }}>×</button>
            </div>
          );
        })}
        {/* Admin previewing/acting-as a client — show on EVERY device (the
            "Preview" buttons put you in client mode, which previewMode alone
            misses) with a one-tap exit back to the console. Compact on mobile so
            the Exit button always fits. */}
        {/* View-as banner: an admin is inside a client USER's session (hint
            cookie set by the server; the session itself carries the authority). */}
        {(() => {
          const m = document.cookie.match(/(?:^|;\s*)howler_viewing_as=([^;]+)/);
          if (!m) return null;
          return (
            <div style={{ ...previewBar, background: 'linear-gradient(90deg, #7C3AED, #A855F7)' }}>
              <Icon name="eye" size={15} />
              <span style={{ fontWeight: 700 }}>Viewing as {decodeURIComponent(m[1])}</span>
              {!isMobile && <span style={{ opacity: 0.85 }}>You see exactly what they see — their role, dashboards and features.</span>}
              <div style={{ flex: 1 }} />
              <button style={exitPreviewBtn} onClick={() => api.impersonateExit().then(() => { window.location.href = '/admin'; }).catch(() => { window.location.href = '/login'; })}>Exit</button>
            </div>
          );
        })()}
        {isAdmin && !!activeEntityId && (
          <div style={previewBar}>
            <Icon name="eye" size={15} />
            <span style={{ fontWeight: 700 }}>Client preview{(() => { const n = suites.find((s) => s.entityId === activeEntityId)?.entityName || active?.name; return n ? ` — ${n}` : ''; })()}</span>
            {!isMobile && <span style={{ opacity: 0.85 }}>You're viewing this exactly as the client would, scoped to their data.</span>}
            <div style={{ flex: 1 }} />
            <button style={exitPreviewBtn} onClick={() => { enterConsole(); navigate('/admin'); }}>Exit preview</button>
          </div>
        )}
        {(isMobile || collapsed) && (
          <div style={menuBar}>
            <button style={menuBtn} onClick={() => (isMobile ? setNavOpen(true) : toggleCollapsed())} title="Menu" aria-label="Open menu">☰</button>
            {/* No back button — Home (and the phone's own back gesture) cover it, and the
                extra arrow just crowded the bar. */}
            <button style={menuBtn} onClick={() => vtNavigate(navigate, previewMode ? '/preview' : '/')} title="Home" aria-label="Home">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 10.5 12 3l9 7.5" />
                <path d="M5 9.5V21h5.5v-6h3v6H19V9.5" />
              </svg>
            </button>
            {/* Dashboard pages show their live tile title; section pages (Alerts,
                Goals, Event Ops…) portal their page title in here via <PageHeader>
                so there's no second home button and no tall header below. */}
            {activeTitle
              ? <span style={{ flex: 1, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTitle}</span>
              : <span ref={setTitleSlot} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }} />}
            {/* Page (dashboard) actions portal in here — Summary · Filters · ⋯ */}
            <div ref={setActionsSlot} style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 'auto', flexShrink: 0 }} />
          </div>
        )}
        <Outlet context={{ previewEntityId: activeEntityId, actionsSlot, titleSlot }} />
      </main>
      {(FEATURES.ask || nativeOwl) && !askOpen && !activeSuiteShared && (
        // Floating owl — quick launcher for the AI drawer (bottom-right). In dual
        // mode (Spark + analyst) tapping it fans out to Spark / Owl; otherwise
        // it opens the one surface directly. Hover/focus pre-warms the analyst
        // (only when the analyst can open) so the first open is instant.
        <>
          {dualOwls && owlDialOpen && (
            <>
              <div onClick={() => setOwlDialOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 54 }} />
              <div style={{ position: 'fixed', bottom: 86, right: isMobile ? 16 : 24, zIndex: 56, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                {[['native', 'spark', 'Assistant', 'Spark — alerts, audiences, campaigns, links, product help'], ['analyst', 'owl', 'Data Analyst', 'The Owl — dig into the numbers']].map(([s, icon, lbl, tip]) => (
                  <button key={s} onClick={() => openAsk(s)} onMouseEnter={() => { if (s === 'analyst') setPrewarmAsk(true); }} title={tip} aria-label={tip}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, padding: '10px 18px', minHeight: 42, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 22px rgba(0,0,0,0.3)' }}>
                    <Icon name={icon} size={16} />{lbl}
                  </button>
                ))}
              </div>
            </>
          )}
          <button
            onClick={() => (dualOwls ? setOwlDialOpen((o) => !o) : openAsk())}
            onMouseEnter={() => { if (!nativeOwl || dualOwls) setPrewarmAsk(true); }}
            onFocus={() => { if (!nativeOwl || dualOwls) setPrewarmAsk(true); }}
            title={dualOwls ? 'Spark & the Owl analyst' : sparkMode ? 'Spark — your Pulse assistant' : 'Ask your AI analyst'}
            aria-label={dualOwls ? 'Spark & the Owl analyst' : sparkMode ? 'Spark — your Pulse assistant' : 'Ask your AI analyst'}
            style={{ position: 'fixed', bottom: 20, right: isMobile ? 16 : 24, zIndex: 55, width: 54, height: 54, borderRadius: '50%', border: '1px solid var(--hairline)', background: 'var(--card)', boxShadow: '0 6px 22px rgba(0,0,0,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <AiMark size={28} sparkle={false} />
          </button>
        </>
      )}
      {nativeOwl && !activeSuiteShared && <OwlChat open={askOpen && askSurface === 'native'} onClose={() => setAskOpen(false)} suiteId={suiteId} entityId={activeEntityId} dashboardId={id} clients={owlClients} events={owlEvents} isAdmin={isAdmin} seed={owlSeed} onSeedUsed={() => setOwlSeed(null)} />}
      {(!nativeOwl || dualOwls) && <AnalystDrawer open={askOpen && askSurface === 'analyst'} prewarm={prewarmAsk} onClose={() => setAskOpen(false)} previewEntityId={activeEntityId} />}
    </div>
  );
}

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
}

// Bottom-left profile: avatar + identity, opening a menu with Integrations
// (clients), the theme toggle and Log out — everything that used to crowd the
// top header.
function ProfileFooter({ user, isAdmin, activeEntityId, brand, helpOn, productOn, journeyOn, onNavigate }) {
  const { theme, toggle } = useTheme();
  const { logout } = useAuth();
  const brandLogoUrl = useBrandLogo(theme); // the active client's resolved branding logo (dark-mode variant when set)
  const [open, setOpen] = useState(false); // false | 'profile' | 'help'
  const entity = user?.entities?.[0];
  const name = isAdmin ? 'Howler · Admin' : (brand?.entityName || entity?.name || (user?.email || '').split('@')[0]);
  // The footer identifies the LOGIN, not the active client (the top-left switcher
  // shows the client). For an admin that's "Howler · Admin" — never a client's
  // logo. Without this gate the logo falls back to user.entities[0].logo (the
  // admin's FIRST linked client, e.g. Kappa), which bleeds the wrong brand in
  // next to "Howler · Admin".
  // Prefer the client's resolved BRANDING logo (what they set under Branding,
  // falling back to their entity logo) so a branding change shows here live.
  const logo = isAdmin ? '' : (brandLogoUrl || brand?.entityLogo || entity?.logo || '');
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div style={{ position: 'relative', borderTop: '1px solid var(--hairline)', padding: 8, flexShrink: 0 }}>
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 70 }} onClick={() => setOpen(false)} />}
      {/* Help menu (the "?" beside the profile) — the occasional-use items that
          used to take a whole block of the sidebar. */}
      {open === 'help' && (
        <div className="modal-in" style={profileMenu}>
          {helpOn && activeEntityId && (
            <button className="nav-row" style={menuItem} onClick={() => { setOpen(false); onNavigate('/help'); }}>
              <Icon name="help" size={17} style={menuIco} /> Help &amp; videos
            </button>
          )}
          {/* Product — report bugs/ideas and track them (everyone, incl. ops-only). */}
          {productOn && (
            <>
              <button className="nav-row" style={menuItem} onClick={() => { setOpen(false); onNavigate('/product'); }}>
                <Icon name="ticket" size={17} style={menuIco} /> Product
              </button>
              <button className="nav-row" style={menuItem} onClick={() => { setOpen(false); openReport(); }}>
                <Icon name="chat" size={17} style={menuIco} /> Report an issue
              </button>
            </>
          )}
          {/* Your journey — stickers, badges & Pulse Points (flag-gated per client). */}
          {journeyOn && (
            <button className="nav-row" style={menuItem} onClick={() => { setOpen(false); onNavigate('/journey'); }}>
              <Icon name="journey" size={17} style={menuIco} /> Your journey
            </button>
          )}
        </div>
      )}
      {open === 'profile' && (
        <div className="modal-in" style={profileMenu}>
          {/* Settings is a client self-service page scoped to the active entity.
              Show it whenever a client profile is in context — for pure clients
              always, and for an admin acting as / previewing a client (so an
              admin who's also an owner of the account can manage it). Admins in
              the bare console (no client in context) don't see it — they use the
              Admin console. */}
          {activeEntityId && (
            <>
              <button className="nav-row" style={menuItem} onClick={() => { setOpen(false); onNavigate('/settings'); }}>
                <Icon name="settings" size={17} style={menuIco} /> Settings
              </button>
              <div style={menuDivider} />
            </>
          )}
          <button className="nav-row" style={menuItem} onClick={toggle}>
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} style={menuIco} /> {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button className="nav-row" style={{ ...menuItem, color: 'var(--error)' }} onClick={() => logout()}>
            <span style={menuIco}>↪</span> Log out
          </button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button className="nav-row" style={{ ...profileRow, flex: 1, minWidth: 0 }} onClick={() => setOpen((v) => (v === 'profile' ? false : 'profile'))} title="Profile & settings">
        {logo ? (
          <img src={logo} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <span style={avatar}>{initial}</span>
        )}
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</span>
        </span>
        <span style={{ color: 'var(--muted)', fontSize: 14, flexShrink: 0 }}>⋯</span>
      </button>
      {((helpOn && activeEntityId) || productOn || journeyOn) && (
        <button className="nav-row" style={helpBtn} onClick={() => setOpen((v) => (v === 'help' ? false : 'help'))} title="Help" aria-label="Help" aria-expanded={open === 'help'}>
          <Icon name="help" size={18} />
        </button>
      )}
      </div>
      <VersionStamp />
    </div>
  );
}

function Ico({ v, size = 16 }) {
  if (!v) return null;
  return v.startsWith('data:')
    ? <img src={v} alt="" style={{ width: size, height: size, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} />
    : <span style={{ fontSize: size - 1, lineHeight: 1, flexShrink: 0 }}>{v}</span>;
}
function Caret({ open, small }) {
  return <span className="nav-caret" style={{ display: 'inline-block', width: 12, fontSize: small ? 8 : 9, color: '#b0b0b6', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>;
}
// One-tap jump to the suite's designated "live" (ticket-sales) dashboard — a
// small red pill on the event's row so no drill-down is needed. `wide` fills the
// row on single-suite mobile (where the suite header itself is hidden).
function LiveBtn({ onClick, title, wide }) {
  return (
    <button onClick={onClick} title={title} aria-label={title} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexShrink: 0,
      width: wide ? '100%' : undefined, cursor: 'pointer',
      background: 'transparent', color: 'var(--success)', border: '1.5px solid var(--success)', borderRadius: 980,
      padding: '3px 10px', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', '--live-ring': 'color-mix(in srgb, var(--success) 55%, transparent)', animation: 'howlerPulse 1.6s ease-out infinite' }} />
      LIVE
    </button>
  );
}

const sidebarShell = { width: 264, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 };
const sidebarStyle = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 10px', position: 'relative' };
const profileRow = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px 10px', borderRadius: 10, color: 'var(--text)' };
const avatar = { flexShrink: 0, width: 30, height: 30, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 45%, #7C3AED 100%)' };
const profileMenu = { position: 'absolute', bottom: 'calc(100% + 6px)', left: 8, right: 8, zIndex: 71, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 10px 36px -8px rgba(0,0,0,0.25)', padding: 6, display: 'flex', flexDirection: 'column', gap: 2 };
const menuItem = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', padding: '9px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--text)', textAlign: 'left' };
const menuIco = { width: 18, textAlign: 'center', fontSize: 14, flexShrink: 0 };
const menuCap = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', padding: '6px 10px 2px' };
const helpBtn = { width: 40, height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', borderRadius: 10, color: 'var(--muted-2)', cursor: 'pointer' };
// Section header in the workspace nav — the whole row toggles open/closed.
const secHeadBtn = { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left' };
const menuDivider = { height: 1, background: 'var(--hairline)', margin: '5px 6px' };
const mobileSidebar = { position: 'relative', zIndex: 51, height: '100%', width: 'min(290px, 84vw)', boxShadow: '4px 0 24px rgba(0,0,0,0.15)', WebkitOverflowScrolling: 'touch' };
const menuBar = { position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--hairline)', background: 'var(--frost)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)' };
const rowBtn = { display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px 12px', borderRadius: 9, fontSize: 14, color: 'var(--text)', lineHeight: 1.3 };
const subRow = { padding: '7px 12px', fontSize: 13 };
const ellip = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dot = { flexShrink: 0, width: 5, height: 5, borderRadius: '50%', display: 'inline-block' };
const countChip = { flexShrink: 0, marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, background: 'rgba(128,128,128,0.18)', color: 'var(--muted-2)', borderRadius: 980, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' };
const tabChip = { flexShrink: 0, marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: 'var(--ai, #7c3aed)', background: 'var(--ai-bg, rgba(124,58,237,0.10))', borderRadius: 980, padding: '2px 7px' };
// An event another client shared with this one (read-only) — see server/suiteShares.js.
const sharedChip = { flexShrink: 0, marginLeft: 'auto', fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--brand)', background: 'rgba(var(--brand-rgb),0.12)', borderRadius: 980, padding: '2px 7px' };
const searchWrap = { display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(128,128,128,0.10)', border: '1px solid var(--hairline)', borderRadius: 9, padding: '6px 10px', margin: '0 4px 10px' };
const searchInput = { flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit' };
const searchClear = { flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 };
// Mobile bottom-sheet nav
const navSheet = { width: '100%', maxHeight: '86dvh', background: 'var(--card)', borderRadius: '18px 18px 0 0', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -10px 40px rgba(0,0,0,0.25)' };
const mRowSuite = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '12px 12px', borderRadius: 11, fontSize: 15, fontWeight: 700, color: 'var(--text)', lineHeight: 1.3 };
const mRowSet = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '11px 12px', borderRadius: 10, fontSize: 14, fontWeight: 600, color: 'var(--muted-2)', lineHeight: 1.3 };
const mRowDash = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '11px 12px', borderRadius: 10, fontSize: 14.5, color: 'var(--text)', lineHeight: 1.3 };
const mHideBtn = { width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', flexShrink: 0 }; // mobile hide/unhide — ≥40px tap target
const menuBtn = { flexShrink: 0, width: 42, height: 42, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 980, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 20, lineHeight: 1, cursor: 'pointer' };
const previewBar = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 16px', background: 'linear-gradient(90deg, var(--brand), var(--brand-2))', color: '#fff', fontSize: 13 };
const exitPreviewBtn = { flexShrink: 0, padding: '6px 14px', borderRadius: 980, border: 'none', background: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const iconBtn = { width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--hairline)', borderRadius: 7, background: 'var(--card)', color: 'var(--muted-2)', fontSize: 12, cursor: 'pointer' };
